/**
 * @file 报名 - 基本信息
 */
import { ButtonAdd, Modal } from '@seiue/ui'
import { Form, Input, InputNumber, Switch } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { OptionReq } from 'packages/sdks-next/growp'

import { CommonBasicInfo } from '@/components/CommonBasicInfo'

import { BasicInfoProps } from '../types'

import { EditOpenContent } from './EditOptionContent'
import { OptionBox } from './components/OptionBox'
import { EntryComponentsWrapper } from 'packages/theme'

/**
 * 报名 - 基本信息
 *
 * @param param0 - 参数
 * @param param0.form - 表单实例
 * @param param0.basicInfoRef - 基本信息 ref
 * @param param0.commonBasicInfo - 基本信息
 * @param param0.setTitle - 设置标题
 * @param param0.activityTaskOptions - 任务选项
 * @param param0.initOptions - 初始选项
 * @param param0.openTypeDisabled - 是否开启开启设置
 * @returns  component
 */
export const BasicInfo: React.FC<
  BasicInfoProps & {
    initOptions: OptionReq[]
    openTypeDisabled?: boolean
  }
> = ({
  form,
  basicInfoRef,
  commonBasicInfo,
  setTitle,
  activityTaskOptions,
  initOptions,
  openTypeDisabled,
}) => {
  const [options, setOptions] = useState<OptionReq[]>(initOptions)

  useEffect(() => {
    if (form !== undefined) {
      form.setFieldValue('options', options)
    }
  }, [form, options])

  return (
    <Wrapper>
      <Form
        colon={false}
        form={form}
        initialValues={{
          isShow: true,
        }}
      >
        <Form.Item
          label="报名标题"
          name="name"
          rules={[{ required: true, message: '请输入报名标题' }]}
        >
          <Input
            placeholder="请输入报名标题"
            onChange={(e) => {
              setTitle && setTitle(e.target.value)
            }}
            maxLength={200}
          />
        </Form.Item>
        <Form.Item
          label="报名简介"
          name="description"
        >
          <TextArea
            maxLength={500}
            showCount={true}
            placeholder="请输入简介"
            autoSize={{
              minRows: 4,
            }}
          />
        </Form.Item>
        <Form.Item
          label="报名选项"
          name="options"
          required={true}
          rules={[{ required: true, message: '报名选项不能为空' }]}
        >
          {options.length > 0 ? (
            <OptionBoxs>
              {options.map((option, key) => {
                return (
                  <OptionBox
                    setOptions={setOptions}
                    options={options}
                    key={key}
                    optionIndex={key}
                    option={option}
                  />
                )
              })}
            </OptionBoxs>
          ) : null}
          <ButtonAdd
            onClick={() => {
              const { hide } = Modal.open({
                width: 640,
                footer: null,
                bodyStyle: {
                  padding: '0',
                },
                title: '添加报名选项',
                children: (
                  <EditOpenContent
                    hide={() => hide()}
                    setOptions={setOptions}
                    options={options}
                    outForm={form}
                  />
                ),
              })
            }}
            style={{
              fontWeight: 400,
            }}
          >
            添加报名选项
          </ButtonAdd>
        </Form.Item>

        <Form.Item
          label="报名设置"
          required={true}
          name="selectLimit"
          rules={[
            {
              validator: (rule, value, callback) => {
                const max = form?.getFieldValue(['settings', 'maxSelectLimit'])
                const min = form?.getFieldValue(['settings', 'minSelectLimit'])
                if (
                  max === undefined ||
                  max === null ||
                  max === 0 ||
                  min === undefined ||
                  min === null ||
                  min === 0
                ) {
                  callback('请填写报名设置')
                }
                if (max < min) {
                  callback('可选数量设置冲突,请调整')
                }
                callback()
              },
            },
          ]}
        >
          <SelectBox>
            <SelectLimitBox>
              <SelectTitleBox>最少选择</SelectTitleBox>
              <Form.Item name={['settings', 'minSelectLimit']}>
                <InputNumber
                  style={{ width: '120px' }}
                  min={1}
                  placeholder="请输入"
                  onBlur={() => form?.validateFields(['selectLimit'])}
                  addonAfter="项"
                />
              </Form.Item>
            </SelectLimitBox>
            <MaxSelectLimitBox>
              <SelectTitleBox>最多选择</SelectTitleBox>
              <Form.Item name={['settings', 'maxSelectLimit']}>
                <InputNumber
                  style={{ width: '120px' }}
                  max={options.length}
                  min={1}
                  placeholder="请输入"
                  onBlur={() => form?.validateFields(['selectLimit'])}
                  addonAfter="项"
                />
              </Form.Item>
            </MaxSelectLimitBox>
          </SelectBox>
        </Form.Item>
        <SwitchBox>
          <Form.Item
            label="允许退选"
            name={['settings', 'allowEdit']}
            extra={'开启后学生可以修改报名结果'}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </SwitchBox>

        <CommonBasicInfo
          form={form}
          ref={basicInfoRef}
          value={commonBasicInfo}
          activityTaskOptions={activityTaskOptions || []}
          openTypeDisabled={!!openTypeDisabled}
        />
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled(EntryComponentsWrapper)`
  &&& {
    .ant-form-item-label {
      width: 77px;
      margin-right: 10px;
      text-align: right;
      & .ant-form-item-no-colon {
        display: flex;
        justify-content: flex-end;
      }
      label[title='报名设置'] {
        height: 14px;
      }
    }
    .ant-form-item {
      margin-bottom: 20px;
    }
    .ant-form-item-extra {
      margin-top: 4px;
      font-size: 12px;
      line-height: 12px;
      color: ${(props) => props.theme.text._3};
    }
  }
`

const OptionBoxs = styled.div`
  min-height: 108px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 12px;
`

const SelectBox = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`

const SelectLimitBox = styled.div`
  height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const MaxSelectLimitBox = styled.div`
  height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const SelectTitleBox = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  color: ${(props) => props.theme.text._2};
`

const SwitchBox = styled.div`
  &&& {
    .ant-switch-checked {
      background-color: ${(props) => props.theme.brand._1} !important;
    }
  }
`
